'use client';

import { useTheme } from '@/lib/theme/useTheme';
import { Sun, Moon } from 'lucide-react';

/**
 * ThemeToggle Component
 *
 * A simple button component to toggle between light and dark themes.
 * Demonstrates the design system theme switching functionality.
 *
 * @example
 * ```tsx
 * import { ThemeToggle } from '@/components/theme-toggle';
 *
 * export default function Header() {
 *   return (
 *     <header>
 *       <ThemeToggle />
 *     </header>
 *   );
 * }
 * ```
 */
export function ThemeToggle() {
  const { theme, toggleTheme, isLoading } = useTheme();

  if (isLoading) {
    return (
      <button
        disabled
        className="p-2 rounded-md bg-neutral-200 dark:bg-neutral-700 text-neutral-400"
        aria-label="Loading theme"
      >
        <div className="w-5 h-5 animate-spin border-2 border-current border-t-transparent rounded-full" />
      </button>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-md bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-200 transition-colors"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        <Moon className="w-5 h-5" />
      ) : (
        <Sun className="w-5 h-5" />
      )}
    </button>
  );
}

/**
 * ThemeToggleWithLabel Component
 *
 * Theme toggle with a text label showing the current theme.
 * Useful for settings pages or preferences.
 */
export function ThemeToggleWithLabel() {
  const { theme, toggleTheme, isLoading } = useTheme();

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-neutral-600 dark:text-neutral-300">
        Theme:
      </span>
      <button
        onClick={toggleTheme}
        disabled={isLoading}
        className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Loading...' : theme === 'light' ? 'Light Mode' : 'Dark Mode'}
      </button>
    </div>
  );
}
